import React from "react"
import type { ReactNode, CSSProperties } from "react"
import cx from "classnames"
import Stack from "../stack"
import Label from "../label"
import Icon from "../icon"
import type { IconName } from "../icon"
import Text from "../text"

type TextareaState =
  | "default"
  | "error"
  | "success"
  | { name: "default" | "error" | "success"; message?: string }

type TextareaResize = "none" | "both" | "vertical" | "horizontal"

export type TextareaProps = {
  /** State for the Textarea can be one of the following: "default", "error", or "success". It can also be an object with the following properties: `name` and `message`. */
  state?: TextareaState
  /** Additional CSS class names */
  className?: string
  /** HTML id attribute for the textarea element */
  id?: string
  /** Inline styles applied to the textarea element */
  style?: CSSProperties
  /** Prevents interaction when true */
  disabled?: boolean
  /** Controls the resize behavior of the textarea */
  resize?: TextareaResize
  /** Number of visible text rows */
  rows?: number
  /** Placeholder text shown when the textarea is empty */
  placeholder?: string
  /** Called when the textarea value changes */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** Called when the textarea loses focus */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
  /** Called when a key is pressed in the textarea */
  onKeyPress?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  /** Controlled value of the textarea */
  value?: string
  /** Default value for uncontrolled usage */
  defaultValue?: string
  /** HTML name attribute for form submission */
  name?: string
  /** Maximum number of characters allowed */
  maxLength?: number
  /** Minimum number of characters required */
  minLength?: number
  /** Makes the textarea non-editable */
  readOnly?: boolean
  /** Automatically focuses the textarea on mount */
  autoFocus?: boolean
  /** Text (or an element) placed below the textarea box on the left. */
  message?: string
  /** Label displayed above the textarea */
  label?: ReactNode
  /** Text (or an element) placed on the right side of the label. */
  info?: ReactNode
}

type StateConfig = {
  inputClasses: string
  icon?: IconName
  iconColor?: string
}

const STATES: Record<string, StateConfig> = {
  default: {
    inputClasses:
      "border border-gray-200 focus:outline-none focus:border-blue-500 focus:ring-0 hover:border-gray-300"
  },
  error: {
    inputClasses: "border-red-600 outline-red",
    icon: "alert-circle",
    iconColor: "text-red-600"
  },
  success: {
    inputClasses: "border-green-600 outline-green",
    icon: "check-circle",
    iconColor: "text-green-600"
  }
}

/**
 * A multi-line text input for longer form content.
 * Supports label, info text, validation states with messages, and configurable resize behavior.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      state = "default",
      className,
      disabled = false,
      style,
      resize = "vertical",
      info,
      message,
      label,
      id,
      rows,
      placeholder,
      onChange,
      onBlur,
      onKeyPress,
      value,
      defaultValue,
      name,
      maxLength,
      minLength,
      readOnly,
      autoFocus
    },
    ref
  ) => {
    const stateName =
      typeof state === "string" ? state : state?.name || "default"
    const stateMessage = typeof state === "object" ? state?.message : undefined

    const resolvedClassName = cx(
      {
        "bg-gray-50 select-none": disabled
      },
      className,
      getResize(resize),
      "text-sm leading-5 px-3 py-2 w-full rounded",
      STATES[stateName].inputClasses
    )

    return (
      <Stack spacing={2}>
        {label && (
          <div className="flex justify-between">
            <Label htmlFor={id}>{label}</Label>
            {info && (
              <Text size="sm" className="text-gray-700 leading-5">
                {info}
              </Text>
            )}
          </div>
        )}
        <textarea
          id={id}
          ref={ref}
          style={{
            ...style,
            transition: "border-color 0.3s",
            resize: resize
          }}
          className={resolvedClassName}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          value={value}
          defaultValue={defaultValue}
          name={name}
          maxLength={maxLength}
          minLength={minLength}
          readOnly={readOnly}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
        {stateMessage && (
          <div className="flex items-center gap-1">
            {STATES[stateName].icon && (
              <Icon
                name={STATES[stateName].icon}
                className={STATES[stateName].iconColor}
              />
            )}
            <Text size="sm" className="text-black">
              {stateMessage}
            </Text>
          </div>
        )}
        {message && (
          <Text size="sm" className="text-gray-700 leading-5">
            {message}
          </Text>
        )}
      </Stack>
    )
  }
)

function getResize(resize: TextareaResize): string {
  switch (resize) {
    case "none":
      return "resize-none"
    case "both":
      return "resize"
    case "vertical":
      return "resize-y"
    case "horizontal":
      return "resize-x"
    default:
      return "resize-none"
  }
}

export { Textarea }
export default Textarea
